<template>
  <AccountDrawer
    v-if="licenseDetailDrawerVisibility && licenseDetailDrawerLicense"
    :key="licenseDetailDrawerLicense?._id"
    v-model:visibility="licenseDetailDrawerVisibility"
    :account="licenseDetailDrawerLicense" />

  <!-- User Drawer -->
  <PeopleDrawer
    v-if="userDetailsDrawerVisibility && userDetailsDrawerUserId"
    v-model:drawer-visibility="userDetailsDrawerVisibility"
    :person-id="userDetailsDrawerUserId"
    @close="userDetailsDrawerUserId = null" />

  <!-- Deactivate License -->
  <AccountDeactivateDialog
    v-if="licenseToDeactivate"
    v-model:visibility="deactivateLicenseDialogVisible"
    :license="licenseToDeactivate"
    @close="deactivateLicenseDialogVisible = false" />

  <!-- Deleting a single license -->
  <DeleteConfirmDialog
    v-if="licenseToDelete"
    v-model:visible="deleteLicenseDialogVisible"
    :loading="deleteLicenseLoading"
    :title="i18n.t('deleteLicence')"
    :sub-title="
      i18n.t('views.applicationDetails.deleteLicenseOf', {
        email: licenseToDelete?.email,
      })
    "
    @cancel="deleteLicenseDialogVisible = false"
    @delete="deleteLicence(licenseToDelete!)" />

  <!-- Edit license -->
  <SmDialog
    v-model:visibility="visibilityEditDialog"
    size="medium"
    :title="
      editLicenseSoftware?.display_name || editLicenseSoftware?.software_name
    ">
    <AccountLicenseEditDialog
      v-if="editLicenseSoftware && editLicenseUser && editLicenseLicense"
      :people="[editLicenseUser]"
      :software-licenses="editLicenseSoftwareLicenses"
      :application="editLicenseSoftware"
      :license="editLicenseLicense"
      @close="handleLicenceEditClose" />
  </SmDialog>

  <!-- Edit License Group -->
  <SmDialog
    v-model:visibility="editLicenseGroupDialog"
    :title="i18n.t('editGroup')">
    <AccountGroupEditContent
      v-if="editLicenseSoftware && editLicenseUser && editLicenseLicense"
      :user="editLicenseUser"
      :license="editLicenseLicense"
      :application-id="editLicenseSoftware._id"
      @close="editLicenseGroupDialog = false"
      @group-edited="() => updateLicenses({ allowCached: false })" />
  </SmDialog>

  <!-- Table -->
  <SmTable
    v-if="isReady && !showEmptyState"
    ref="table"
    :data="data"
    :columns="columns"
    :loading="loading"
    :selectable="props.select"
    :style="{
      height: `calc(100% - ${currentApplication?.sub_accounts ? 50 : 0}px)`,
    }"
    key-field="_id"
    :load-all-fn="getAllLicences"
    :contain-strict="props.containStrict"
    @scroll-end-reached="loadMoreLicences"
    @cell-click="handleCellClick">
    <!-- Software -->
    <template #software_id="{ row }">
      <div class="application_name">
        <ApplicationIcon
          class="shrink-0"
          :size="23"
          :application-name="
            getApplication(row.software_id)?.software_name ?? ''
          "
          :application-type="
            getApplication(row.software_id)?.type || undefined
          " />

        <TextMultiline
          :text="getApplicationDisplayName(row.software_id)"
          :show-tool-tip="false" />
      </div>
    </template>

    <!-- Email -->
    <template #email="{ row }">
      <!-- Alias Selector -->
      <AliasSelect
        v-if="!row.account_id"
        :license="row"
        :get-options="getAliasOptions"
        @license-changed="updateLicenses()" />

      <!-- Selected username -->
      <div
        v-else-if="currentApplication?.information.returns_usernames"
        class="py-4">
        <TextMultiline :text="getPersonById(row.account_id)?.email" />
        <TextMultiline :text="row.email" class="text-contrast-muted" />
      </div>

      <!-- Default: Email -->
      <TextMultiline
        v-else
        :text="row.email"
        class="email-text"
        :show-tool-tip="false" />

      <!-- Guest -->
      <span v-if="row?.is_guest" class="guest"> ({{ i18n.t('guest') }})</span>
    </template>

    <!-- Phone Number -->
    <template #phone="{ row }">
      <span v-if="row.phone_numbers.length > 0">{{
        row.phone_numbers[0]
      }}</span>
    </template>

    <template #software_licenses="{ row }">
      <LicenceModelNamesDisplay
        :license-names="
          getLicenseNames(Object.keys(row.software_licenses || {}))
        " />
    </template>

    <!-- SubAccount -->
    <template #sub_account_id="{ row }">
      <span
        v-if="
          row.sub_account_id && getApplication(row.software_id)?.sub_accounts
        ">
        {{
          // @ts-ignore-next-line undefined check is made in the v-if
          getApplication(row.software_id)?.sub_accounts[row.sub_account_id]
            .name || row.sub_account_id
        }}
      </span>
    </template>

    <!-- Status -->
    <template #status="{ row }">
      <AccountStatusSmall v-if="row.status" :status="row.status" />
    </template>

    <!-- Last Login -->
    <template #last_login_time="{ row }">
      <TextMultiline v-if="row.last_login_time">
        {{ displayDate(new Date(row.last_login_time), { dateStyle: 'short' }) }}
      </TextMultiline>
      <span v-else class="no-data">
        {{ i18n.t('views.licences.table.neverLoggedIn') }}
      </span>
    </template>

    <!-- Price -->
    <template #price="{ row }">
      <TextMultiline v-if="row._id! in costs">
        {{ getCorrectedPrice(costs[row._id!]) }}
      </TextMultiline>
      <span v-else>-</span>
    </template>

    <!-- statusIndicator -->
    <template #statusIndicator="{ row }">
      <AccountStatusSmall
        v-if="row.status"
        :status="row.status"
        :show-text="false" />
    </template>

    <!-- License Count -->
    <template #license_count="{ row }">
      <span
        v-if="row.software_licenses"
        :class="{ 'no-data': Object.keys(row.software_licenses).length === 0 }">
        {{
          i18n.t('general.licenses', Object.keys(row.software_licenses).length)
        }}
      </span>
      <span v-else class="no-data">
        {{ i18n.t('general.licenses', 0) }}
      </span>
    </template>

    <!-- Custom Columns -->
    <template v-for="column in props.customColumns" #[column.key]="{ row }">
      <slot :name="column.key" :row="row" />
    </template>

    <template #action="{ row }">
      <SmDropdown trigger="click">
        <!-- Show -->
        <SmDropdownItem :icon="File" @item-click="showLicense(row)">
          {{ i18n.t('showAccount') }}
        </SmDropdownItem>
        <!-- Edit -->
        <SmDropdownItem
          v-if="softwareLicenseAssignable && row.account_id"
          :icon="PencilLine"
          @item-click="showEditLicence(row)">
          {{ i18n.t('editLicence') }}
        </SmDropdownItem>

        <!-- Edit Groups -->
        <SmDropdownItem
          v-if="isGroupEdit(row.software_id)"
          v-require-permission="CompanyAccessRole.ADMIN"
          :disabled="isApplicationReadOnly(row.software_id)"
          :tooltip="readonlyTooltip"
          :icon="Users"
          @item-click="showEditGroup(row)">
          {{ i18n.t('editGroup') }}
        </SmDropdownItem>

        <!-- Show User -->
        <SmDropdownItem
          v-if="row.account_id"
          :icon="User"
          @item-click="row.account_id ? showUser(row.account_id) : null">
          {{ i18n.t('showUser') }}
        </SmDropdownItem>

        <!-- Activate License -->
        <SmDropdownItem
          v-if="
            applicationSupportsDisable(row.software_id) &&
            row.status === 'disabled'
          "
          v-require-permission="CompanyAccessRole.ADMIN"
          :icon="KeyRound"
          :disabled="isApplicationReadOnly(row.software_id)"
          :tooltip="readonlyTooltip"
          hover-color="var(--sm-primary)"
          @item-click="handleLicenseDeactivate(row)">
          {{ i18n.t('activateAccount') }}
        </SmDropdownItem>

        <!-- Deactivate License -->
        <SmDropdownItem
          v-if="
            applicationSupportsDisable(row.software_id) &&
            row.status !== 'disabled'
          "
          v-require-permission="CompanyAccessRole.ADMIN"
          :disabled="isApplicationReadOnly(row.software_id)"
          :tooltip="readonlyTooltip"
          :icon="Ban"
          hover-color="var(--sm-orange)"
          @item-click="handleLicenseDeactivate(row)">
          {{ i18n.t('deactivateAccount') }}
        </SmDropdownItem>

        <!-- Delete License -->
        <SmDropdownItem
          v-if="applicationSupportsDelete(row.software_id)"
          v-require-permission="CompanyAccessRole.ADMIN"
          :icon="Trash"
          hover-color="var(--sm-magenta)"
          :tooltip="readonlyTooltip"
          :disabled="isApplicationReadOnly(row.software_id)"
          @item-click="handleLicenseDelete(row)">
          {{ i18n.t('deleteLicence') }}
        </SmDropdownItem>
      </SmDropdown>
    </template>

    <template v-if="selection && selection?.length > 0" #footer>
      <AccountTableBulkEdit
        :selection="selection"
        :available-licenses="filteredLicenseModelRows"
        :total-accounts="initialFilteredCount ?? 0"
        :permissions="currentApplication?.information"
        :software-license-assignable="softwareLicenseAssignable ?? false"
        @request-licences-refetch="updateLicenses()"
        @reset-selection="resetSelection()"
        @update-count="updateInitFilterCount()" />
    </template>
  </SmTable>

  <EmptyState
    v-else-if="showEmptyState"
    :icon="File"
    :title="i18n.t('accountsEmptyStateTitle')"
    :text="i18n.t('accountsEmptyStateText')"
    :show-docs-btn="false"
    :show-create-btn="false" />
</template>

<script lang="ts" setup>
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    ApiError,
    CompanyAccessRole,
    Licence,
    LicenceOut,
    SoftwareCostService,
    SoftwareLicense,
    SoftwareLicensesService,
    SoftwareOut,
  } from '@/client'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { useApplicationReauthStore } from '@/stores/applicationReauthStore'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
  import { displayDate } from '@/utils/formatter/date'
  import { useGlobalStore } from '@/stores/globalStore'
  import { useRoute } from 'vue-router'
  import { sendToast } from '@/components/sm/SmNotification'
  import { Column } from '@/components/sm/SmTable.types'
  import SmTable from '@/components/sm/SmTable.vue'
  import { capitalize } from '@/utils/formatter/formatter'
  import ApplicationIcon from '@/components/applications/ApplicationIcon.vue'
  import { AccountStore } from '@/stores/account/account.store'
  import { ApplicationStore } from '@/stores/application/application.store'
  import {
    AccountFilters,
    applyAccountFilters,
  } from '@/stores/account/account.filter'
  import { PersonStore } from '@/stores/person/person.store'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import {
    PencilLine,
    Users,
    File,
    User,
    KeyRound,
    Ban,
    Trash,
  } from 'lucide-vue-next'
  import { Option } from '@/components/sm/SmSelect.vue'
  import type { LicenceOutTable } from '@/stores/account/account.models'

  const i18n = useI18n()
  const route = useRoute()
  const applicationStore = useApplicationReauthStore()
  const preferenceStore = usePreferenceStore()
  const table = ref<ComponentExposed<typeof SmTable<Licence>>>()
  const isReady = ref(false)

  // Edit License
  const editLicenseUser = ref<Account>()
  const editLicenseSoftware = ref<SoftwareOut>()
  const editLicenseLicense = ref<Licence>()
  const visibilityEditDialog = ref(false)
  const editLicenseGroupDialog = ref(false)
  const deleteLicenseDialogVisible = ref(false)
  const licenseToDelete = ref<Licence>()
  const deleteLicenseLoading = ref(false)
  const isSSO = computed(() => route.query.sso === 'true')
  const currentSubaccount = computed(() => route.params.subAccountId as string)

  // Deactivate License
  const deactivateLicenseDialogVisible = ref(false)
  const licenseToDeactivate = ref<Licence>()

  const softwareLicenseAssignable = computed(() => {
    if (currentApplication.value?.information.sso_provider) {
      return false
    }
    if (
      softwareLicenses.value.some((license) => !license.external_id) ||
      currentApplication.value?.information.implements_update_method
    ) {
      return true
    }
  })

  interface Props {
    searchQuery?: string
    applicationId?: string
    subAccountId?: string
    accountId?: string
    displayApplicationName?: boolean
    displayStatus?: boolean
    filter?: AccountFilters
    displayPrice?: boolean
    displayStatusIndicator?: boolean
    displayLicenceCount?: boolean
    displayLastLoggedIn?: boolean
    displayPhoneNumber?: boolean
    select?: boolean
    displaySubAccount?: boolean
    displayEmail?: boolean
    onlyCached?: boolean
    displayName?: boolean
    customColumns?: Column<Licence>[]
    displayActions?: boolean
    clickAction?: (row: Licence) => void
    licenses?: SoftwareLicense[]
    containStrict?: boolean
  }

  //   Props with default values
  const props = withDefaults(defineProps<Props>(), {
    searchQuery: undefined,
    applicationId: undefined,
    subAccountId: undefined,
    accountId: undefined,
    displayApplicationName: false,
    displayStatus: false,
    displayPrice: false,
    displayStatusIndicator: false,
    displayLicenceCount: false,
    displayLastLoggedIn: true,
    select: false,
    displaySubAccount: false,
    displayEmail: true,
    displayName: false,
    displayPhoneNumber: false,
    customColumns: () => [],
    displayActions: true,
    clickAction: undefined,
    filter: undefined,
    onlyCached: false,
    licenses: undefined,
    containStrict: true,
  })

  const emit = defineEmits<{ licensesChanged: [] }>()
  const applicationDetailsStore = useApplicationDetailsStore()

  function handleCellClick(_: number, columnKey: string, row: Licence) {
    // Index is not used
    if (props.clickAction) {
      props.clickAction(row)
      return
    } else if (columnKey === 'selection') {
      // do nothing
    } else if (columnKey === 'action') {
      // do nothing
    } else if (columnKey === 'email' && !row.account_id) {
      // do nothing
    } else {
      showLicense(row)
    }
  }

  const columns = computed(() => {
    let _columns: Column<Licence>[] = []

    if (props.displayStatusIndicator) {
      //   Add status column as first column
      _columns.push({
        key: 'statusIndicator',
        label: '',
        width: '10px',
        sortable: false,
      })
    }

    if (props.displayEmail) {
      if (!_columns.find((c) => c.key === 'email')) {
        _columns.push({
          key: 'email',
          label: i18n.t('views.licences.table.email') as string,
          width: 1,
          sortFn: (a: Licence, b: Licence) => {
            return a.email.toLowerCase().localeCompare(b.email.toLowerCase())
          },
          footer: ref(
            i18n.t('accountCount', { count: countFilteredAccounts.value })
          ),
          loader: true,
        })
      }
    }

    if (props.displayApplicationName) {
      _columns.push({
        key: 'software_id',
        label: capitalize(i18n.t('application', { count: 2 })),
        width: 1,
        loader: true,
        sortFn: (a: Licence, b: Licence) => {
          return getApplicationSortName(a.software_id).localeCompare(
            getApplicationSortName(b.software_id)
          )
        },
      })
    }

    if (props.displayStatus) {
      //   Add status column as third last column
      _columns.push({
        key: 'status',
        label: 'Status',
        width: 1,
        loader: true,
      })
    }

    if (props.displaySubAccount && atLeastOneSubAccount.value) {
      //   Add sub account column as second last column
      _columns.push({
        key: 'sub_account_id',
        label: i18n.t('subAccount') as string,
        width: 1,
      })
    } else {
      _columns = _columns.filter((c) => c.key !== 'sub_account')
    }

    // If name is in any license, add it
    if (props.displayName && data.value.some((l) => l.name)) {
      _columns.push({
        key: 'name',
        label: i18n.t('name') as string,
        width: 1,
        loader: true,
      })
    }

    if (props.displayPhoneNumber) {
      _columns.push({
        key: 'phone',
        label: i18n.t('phone') as string,
        width: 1,
        loader: true,
      })
    }

    if (props.displayPrice) {
      //   Add price column as second last column
      _columns.push({
        key: 'price',
        label: i18n.t('price'),
        sortable: false,
        width: 1,
        sortFn: (a: Licence, b: Licence) => {
          return costs.value[a._id!] - costs.value[b._id!]
        },
        footer: ref(totalCosts.value ?? 0),
        loader: true,
      })
    }

    if (displayLastLogin.value && props.displayLastLoggedIn) {
      //   Add last login column as second last column
      _columns.push({
        key: 'last_login_time',
        label: i18n.t('views.licences.table.lastLogin') as string,
        width: 1,
        loader: true,
      })
    }

    if (props.displayLicenceCount) {
      //   Add licence count column as second last column
      _columns.push({
        key: 'license_count',
        label: i18n.t('views.licences.table.licenseCount') as string,
        width: 1,
        sortable: true,
        sortFn: (a: Licence, b: Licence) => {
          return (
            Object.keys(a.software_licenses || {}).length -
            Object.keys(b.software_licenses || {}).length
          )
        },
      })
    }

    _columns.push({
      key: 'software_licenses',
      label: i18n.t('views.licences.table.licenceModel') as string,
      width: 1,
      sortable: false,
      loader: true,
    })

    _columns = [..._columns, ...props.customColumns]

    if (props.displayActions) {
      //   Add action column as last column
      _columns.push({
        key: 'action',
        label: '',
        width: '60px',
        sortable: false,
      })
    }

    return _columns
  })

  const loading = computed(() => {
    return loadingAccounts.value || loadingApplications.value
  })

  const licenseDetailDrawerVisibility = ref(false)
  const licenseDetailDrawerLicense = ref<Licence>()

  const userDetailsDrawerVisibility = ref(false)
  const userDetailsDrawerUserId = ref<string | null>(null)

  const { data: applications, loading: loadingApplications } =
    ApplicationStore.useApplications<SoftwareOut>()

  const applicationsById = computed(() => {
    const map = new Map<string, SoftwareOut>()
    for (const application of applications.value) {
      map.set(application._id, application)
    }
    return map
  })

  function getApplication(softwareId?: string | null) {
    if (!softwareId) return undefined
    return applicationsById.value.get(softwareId)
  }

  function getApplicationDisplayName(softwareId?: string | null) {
    const application = getApplication(softwareId)
    return application?.display_name || application?.software_name || ''
  }

  function getApplicationSortName(softwareId?: string | null) {
    return getApplication(softwareId)?.software_name?.toLowerCase() || ''
  }

  function isApplicationReadOnly(softwareId?: string | null) {
    return Boolean(getApplication(softwareId)?.read_only)
  }

  function applicationSupportsDisable(softwareId?: string | null) {
    return Boolean(
      getApplication(softwareId)?.information?.implements_disable_licence_method
    )
  }

  function applicationSupportsDelete(softwareId?: string | null) {
    return Boolean(
      getApplication(softwareId)?.information?.implements_delete_user_method
    )
  }

  const softwareLicenses = ref<SoftwareLicense[]>([])

  const licenseNameMap = computed(() => {
    const map = new Map<string, string>()
    for (const license of softwareLicenses.value) {
      map.set(license._id, license.name)
    }
    return map
  })

  const readonlyTooltip = computed(() => {
    if (currentApplication.value?.read_only) {
      return i18n.t('readonlyDesc')
    }
    return false
  })

  // ######################################
  // ############# Pagination #############
  // ######################################

  const INITIAL_ROW_LIMIT = 300
  const ROW_EXPAND_COUNT = 100

  const filter = computed(() => {
    return {
      applicationId: props.applicationId,
      subAccountId: props.subAccountId,
      ...props.filter,
    }
  })

  watch(filter, () => {
    updateParameters(filter.value)
  })

  const {
    data,
    loading: loadingAccounts,
    updateParameters,
  } = AccountStore.useAccounts(
    filter,
    { limit: INITIAL_ROW_LIMIT },
    { column: 'email' }
  )

  const { count: countFilteredAccounts } = AccountStore.useAccountsCount(filter)

  const showEmptyState = computed(() => {
    return (
      isReady.value &&
      !loading.value &&
      data.value.length === 0 &&
      (initialFilteredCount.value ?? 0) === 0
    )
  })

  async function loadMoreLicences() {
    updateParameters({}, { limit: data.value.length + ROW_EXPAND_COUNT })
  }

  async function getAllLicences() {
    const allLicenses = await AccountStore.getAccounts(filter.value)
    return allLicenses
  }

  const { count: totalLicensesCount } = AccountStore.useAccountsCount()
  const initialFilteredCount = ref<number | undefined>()

  function updateInitFilterCount() {
    AccountStore.getAccountsCount(filter.value).then((r) => {
      initialFilteredCount.value = r
    })
  }

  const SORTABLE_COLUMNS = [
    'email',
    'software_id',
    'status',
    'sub_account_id',
    'last_login_time',
    'created',
    'updated',
    'name',
  ] as const satisfies ReadonlyArray<keyof LicenceOutTable>

  type SortableColumn = (typeof SORTABLE_COLUMNS)[number]

  function isSortableColumn(
    column: string | undefined
  ): column is SortableColumn {
    if (!column) return false
    return SORTABLE_COLUMNS.some((sortableColumn) => sortableColumn === column)
  }

  // Watch for changes in the sorting
  const sorting = computed(() => {
    const direction: 'asc' | 'desc' = table.value?.sorting.asc ? 'asc' : 'desc'
    const sortBy = table.value?.sorting.by

    if (isSortableColumn(sortBy)) {
      return {
        column: sortBy,
        direction,
      }
    }

    return { direction }
  })

  watch(
    () => sorting.value,
    (newVal) => {
      if (loading.value) return
      updateParameters({}, {}, newVal)
    }
  )

  // ######################################
  // ############# Cost Calculation #######
  // ######################################

  const costs = ref<Record<string, number>>({})

  const filteredIds = ref<{ _id: string | null }[]>([])

  const filteredCosts = computed(() => {
    const ids = new Set(filteredIds.value.map(({ _id }) => _id))

    return Object.fromEntries(
      Object.entries(costs.value).filter(([key]) => ids.has(key))
    )
  })

  const totalCosts = computed(() => {
    return getCorrectedPrice(
      Object.values(filteredCosts.value).reduce((a, b) => a + b, 0)
    )
  })

  const atLeastOneSubAccount = computed(() => {
    // Check if in licenses at least one sub account is present
    return data.value.some((licence) => licence.sub_account_id)
  })

  const users = ref<Account[]>([])

  const usersById = computed(() => {
    const map = new Map<string, Account>()
    for (const user of users.value) {
      if (user._id) {
        map.set(user._id, user)
      }
    }
    return map
  })

  const userLoadPromises = new Map<string, Promise<Account | null>>()

  async function ensureUsersLoaded(ids: Array<string | null | undefined>) {
    const pendingPromises: Promise<Account | null>[] = []

    for (const id of ids) {
      if (!id) continue
      if (usersById.value.has(id)) continue

      let promise = userLoadPromises.get(id)
      if (!promise) {
        promise = PersonStore.getPersonById(id)
        userLoadPromises.set(id, promise)
      }
      pendingPromises.push(promise)
    }

    if (pendingPromises.length === 0) return

    const fetchedUsers = await Promise.all(pendingPromises)

    let hasChanges = false
    for (const fetched of fetchedUsers) {
      if (fetched && !usersById.value.has(fetched._id)) {
        users.value.push(fetched)
        hasChanges = true
      }
    }

    if (hasChanges) {
      users.value = [...users.value]
    }
  }

  // Check if last Login should be displayed
  // PERF: This could be faster if we just take the first one with a last login
  const displayLastLogin: Ref<boolean> = ref(true)

  function handleLicenseDelete(l: Licence) {
    deleteLicenseDialogVisible.value = true
    licenseToDelete.value = l
  }

  function handleLicenseDeactivate(l: Licence) {
    deactivateLicenseDialogVisible.value = true
    licenseToDeactivate.value = l
  }

  function showLicense(licence: Licence) {
    licenseDetailDrawerLicense.value = licence
    licenseDetailDrawerVisibility.value = true
  }

  function showUser(id: string) {
    userDetailsDrawerUserId.value = id
    userDetailsDrawerVisibility.value = true
  }

  const filteredLicenseModelRows = computed(() => {
    if (isSSO.value) {
      return softwareLicenses.value.filter(
        (license) => license.sub_account_id == currentSubaccount.value || null
      )
    }
    return softwareLicenses.value
  })

  const editLicenseSoftwareLicenses = computed(() => {
    if (!editLicenseSoftware.value) return []

    const filteredLicenses = softwareLicenses.value.filter((sl) => {
      return (
        sl.software_id === editLicenseSoftware.value?._id &&
        (sl.sub_account_id === editLicenseLicense.value?.sub_account_id ||
          sl.sub_account_id === null)
      )
    })

    return filteredLicenses
  })

  const currentApplication = computed(() => {
    if (!props.applicationId) return undefined
    return getApplication(props.applicationId)
  })

  function isGroupEdit(softwareId: string) {
    if (
      currentApplication.value?.information
        .implements_assign_licence_to_licence_group_method
    ) {
      return true
    }

    return Boolean(
      getApplication(softwareId)?.information
        ?.implements_assign_licence_to_licence_group_method
    )
  }

  function deleteLicence(licence: Licence) {
    if (!licence?._id) return // _id should always be set
    deleteLicenseLoading.value = true

    const removedIds = Object.keys(licence.software_licenses || {})

    AccountStore.deleteAccount(licence._id)
      .then(async () => {
        applicationDetailsStore.updateQuota([], removedIds)
        sendToast(i18n.t('deleteAccountSuccess'), '', 'success')
        deleteLicenseDialogVisible.value = false
      })
      .finally(() => {
        deleteLicenseLoading.value = false
        table.value?.deselect(licence)
        updateInitFilterCount()
        emit('licensesChanged')
      })
  }

  function resetSelection() {
    table.value?.resetSelection()
  }

  // #region Update Licenses
  async function updateLicenses({ allowCached = props.onlyCached } = {}) {
    AccountStore.refreshAccountCacheFromApi(
      {
        applicationId: props.applicationId,
        subAccountId: props.subAccountId,
      },
      allowCached
    )
      .then(async ({ error }) => {
        if (error instanceof ApiError && props.applicationId) {
          switch (error?.body?.detail?.error) {
            case 'error.integration.authentication_required':
            case 'error.integration.bot.state_expired':
            case 'error.integration.bot.invalid_csrf_token':
            case 'error.integration.invalid_oauth_token':
            case 'error.integration.invalid_bot_credentials':
            case 'error.integration.missing_credentials':
            case 'error.integration.required_mfa':
            case 'error.integration.invalid_token_credentials': {
              if (props.applicationId) openReauthDialog(props.applicationId)
              break
            }
          }
        }
      })
      .finally(() => {
        emit('licensesChanged')
      })
  }

  async function openReauthDialog(appId: string) {
    const app = await ApplicationStore.getApplicationById(appId)
    if (app === null) throw new Error('Software not found')
    applicationStore.reauthApplicationToHandle = {
      application: app,
      callback: () => {
        updateLicenses()
      },
    }
    const store = useGlobalStore()
    store.state.showReauthenticateDialog = true
  }

  function getPersonById(id: string) {
    return usersById.value.get(id)
  }

  const aliasOptions = ref<Option<string>[]>([])
  const aliasOptionsLoaded = ref(false)
  let aliasOptionsLoader: Promise<void> | null = null

  function ensureAliasOptionsLoaded() {
    if (aliasOptionsLoaded.value || aliasOptionsLoader) return

    aliasOptionsLoader = PersonStore.getPersons<Account>(
      { isDeleted: false },
      {},
      {},
      false
    )
      .then((response) => {
        aliasOptions.value.splice(
          0,
          aliasOptions.value.length,
          ...response.map((u) => ({
            value: u.email,
            label: u.email,
          }))
        )
        aliasOptionsLoaded.value = true
      })
      .finally(() => {
        aliasOptionsLoader = null
      })
  }

  function getAliasOptions() {
    ensureAliasOptionsLoaded()
    return aliasOptions.value
  }

  const selection = computed(() => {
    return table.value?.selection
  })

  async function showEditLicence(licence: Licence) {
    // Get user
    if (!licence.account_id) return
    const user = await PersonStore.getPersonById(licence.account_id)
    // Get software
    const app = await ApplicationStore.getApplicationById(licence.software_id)
    if (app === null) throw new Error('Application not found')
    editLicenseLicense.value = licence

    editLicenseSoftware.value = app
    editLicenseUser.value = user ?? undefined
    visibilityEditDialog.value = true
  }

  async function refetchSoftwareLicenses() {
    if (props.applicationId)
      softwareLicenses.value =
        await SoftwareLicensesService.getSoftwareLicensesForSoftwareApiV1SoftwareSoftwareSoftwareIdLicensesGet(
          { softwareId: props.applicationId }
        )
    else
      softwareLicenses.value =
        await SoftwareLicensesService.getSoftwareLicensesApiV1SoftwareSoftwareLicensesGet(
          {}
        )
  }

  async function showEditGroup(licence: Licence) {
    // Get user
    if (!licence.account_id) return
    const user = await PersonStore.getPersonById(licence.account_id)
    // Get software
    const application = await ApplicationStore.getApplicationById(
      licence.software_id
    )
    if (application === null) throw new Error('Application not found')
    editLicenseLicense.value = licence
    editLicenseSoftware.value = application
    editLicenseUser.value = user ?? undefined
    editLicenseGroupDialog.value = true
  }

  function getLicenseNames(licenseIds: string[]) {
    const names: string[] = []
    for (const id of licenseIds) {
      const name = licenseNameMap.value.get(id)
      if (name) {
        names.push(name)
      }
    }
    return names
  }

  onMounted(() => {
    updateInitFilterCount()

    if (props.displayPrice) {
      watch(
        filter,
        async () => {
          const query = AccountStore.getDatabase().select('_id')
          const newQuery = applyAccountFilters(query, filter.value)
          filteredIds.value = await newQuery.execute()
        },
        { immediate: true }
      )
    }

    watch(
      data,
      async (rows) => {
        await ensureUsersLoaded(rows.map((licence) => licence.account_id))

        const query = AccountStore.getDatabase().select(({ fn }) => [
          fn.count<number>('last_login_time').as('count'),
        ])

        const newQuery = applyAccountFilters(query, filter.value)

        const result = await newQuery.execute()

        displayLastLogin.value = result[0].count > 0
      },
      { immediate: true }
    )

    // Load costs
    if (props.displayPrice) {
      preferenceStore.getPreferences().then(() => {
        watch(
          () => preferenceStore.preferences?.currency_code,
          () => {
            SoftwareCostService.getLicencesCostsApiV1SoftwareLicencesCostsGet({
              targetCurrencyCode: preferenceStore.preferences?.currency_code,
            }).then((response) => {
              costs.value = response
            })
          },
          { immediate: true }
        )
      })
    }

    if (props.licenses) {
      softwareLicenses.value = props.licenses
    } else {
      refetchSoftwareLicenses()
    }

    // Refresh licenses uncached from vendor
    if (
      props.applicationId &&
      props.subAccountId &&
      route.query.sso !== 'true'
    ) {
      updateLicenses()
    }
    isReady.value = true
  })

  watch(
    () => props.licenses,
    (newLicenses) => {
      if (newLicenses) {
        softwareLicenses.value = newLicenses
      }
    }
  )

  // Exposes
  defineExpose({
    updateLicenses,
    selection,

    resetSelection,
    licences: data,
    totalLicensesCount,
    softwareLicenses,
    refetchSoftwareLicenses,

    // Counts
    filteredCount: countFilteredAccounts,
  })

  function handleLicenceEditClose() {
    visibilityEditDialog.value = false
  }

  watch(visibilityEditDialog, (newVal) => {
    if (!newVal) {
      updateLicenses()
    }
  })

  //#endregion
</script>

<style lang="scss" scoped>
  .delete-user {
    color: var(--el-color-danger);
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .integration_icon {
    height: 23px;
    width: 23px;
  }

  .application_name {
    display: flex;
    gap: 10px;
    align-items: center;
    font-weight: 500;
  }

  .alias-select-wrapper {
    width: clamp(150px, 90%, 250px);
  }

  .guest {
    color: theme('colors.gray[400]');
    margin-left: 5px;
    font-style: italic;
    // No Line Break
    white-space: nowrap;
    word-wrap: break-word;
  }
</style>
